import { useState } from 'react'
import { api } from '../api/client'
import type { Profile } from '../types/profile'

interface Props {
  profiles: Profile[]
  onLaunched: () => void
  onDeleted: () => void
}

export function ProfileList({ profiles, onLaunched, onDeleted }: Props) {
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function launch(id: string, headless: boolean) {
    setBusyId(id)
    setError(null)
    try {
      await api.launchProfile(id, { headless })
      onLaunched()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function remove(id: string, name: string) {
    if (!confirm(`确认删除 profile「${name}」？该操作不可恢复。`)) return
    setBusyId(id)
    setError(null)
    try {
      await api.deleteProfile(id)
      onDeleted()
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  if (profiles.length === 0) {
    return (
      <div className="empty-state">
        <p>暂无 profile。点击「新建」创建第一个。</p>
      </div>
    )
  }

  return (
    <div>
      {error && <div className="error-banner">{error}</div>}
      <div className="profile-grid">
        {profiles.map((p) => (
          <div className="profile-card" key={p.id}>
            <div className="profile-card-header">
              <h3>{p.name}</h3>
              {p.group && <span className="badge">{p.group}</span>}
            </div>
            <dl className="profile-meta">
              <dt>平台</dt>
              <dd>{p.fingerprint.platform} · {p.fingerprint.brand}</dd>
              <dt>语言</dt>
              <dd>{p.fingerprint.language}</dd>
              <dt>时区</dt>
              <dd>{p.fingerprint.timezone}</dd>
              <dt>分辨率</dt>
              <dd>{p.fingerprint.screenWidth}×{p.fingerprint.screenHeight}</dd>
              <dt>CPU 核数</dt>
              <dd>{p.fingerprint.hardwareConcurrency}</dd>
              <dt>种子</dt>
              <dd><code>{p.fingerprint.seed}</code></dd>
              {p.proxy?.enabled && (
                <>
                  <dt>代理</dt>
                  <dd className="proxy">{p.proxy.server}</dd>
                </>
              )}
            </dl>
            {p.tags.length > 0 && (
              <div className="tags">
                {p.tags.map((t) => (
                  <span key={t} className="tag">{t}</span>
                ))}
              </div>
            )}
            <div className="profile-card-actions">
              <button
                onClick={() => launch(p.id, false)}
                disabled={busyId === p.id}
              >
                启动（有界面）
              </button>
              <button
                onClick={() => launch(p.id, true)}
                disabled={busyId === p.id}
              >
                启动（无界面）
              </button>
              <button
                className="danger"
                onClick={() => remove(p.id, p.name)}
                disabled={busyId === p.id}
              >
                删除
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}